// Bloc de To Do's: hojas y pendientes en la base (migración 20261009120000_todo.sql).
import type { SupabaseClient } from '@supabase/supabase-js';
import { limpiarTrazos, type Trazo } from '../logica/trazos';
import { alertasTodo, nuevosDeAlerta, paraManana, type FilaTodo, type HojaTodo, type PendienteTodo } from '../logica/todo';

export interface Hoja extends HojaTodo { trazos: Trazo[] }
export interface DatosTodo { hojas: Hoja[]; pendientes: PendienteTodo[] }
// La base todavía no tiene las tablas (la migración se aplica aparte): la app lo dice y sigue funcionando.
export class SinMigracion extends Error { constructor() { super('Las tablas del bloc de To Do\'s todavía no están en la base. Felipe debe aplicar la migración.'); } }
type Err = { message: string; code?: string } | null;
const revisar = (error: Err) => {
  if (!error) return;
  if (error.code === 'PGRST205' || error.code === '42P01' || /todo_(hojas|pendientes)/.test(error.message)) throw new SinMigracion();
  throw new Error(error.message);
};
const CAMPOS_P = 'id, hoja_id, texto, orden, hecho, fecha_seguimiento, archivado_en, pedido_id';
const aPendiente = (r: Record<string, unknown>): PendienteTodo => ({
  id: String(r.id), hoja_id: String(r.hoja_id), texto: String(r.texto ?? ''), orden: +(r.orden as number) || 0, hecho: !!r.hecho,
  fecha_seguimiento: (r.fecha_seguimiento as string | null) || null, archivado_en: (r.archivado_en as string | null) || null, pedido_id: (r.pedido_id as string | null) || null,
});

// Todo lo del bloc: hojas con sus dibujos y todos los pendientes (también hechos y archivados).
export async function cargarTodo(sb: SupabaseClient): Promise<DatosTodo> {
  const h = await sb.from('todo_hojas').select('id, nombre, orden, trazos, archivada_en').order('orden').order('id');
  revisar(h.error);
  const filas: Record<string, unknown>[] = [];
  for (let a = 0; ; a += 1000) {
    const { data, error } = await sb.from('todo_pendientes').select(CAMPOS_P).order('orden').order('id').range(a, a + 999);
    revisar(error);
    filas.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return {
    hojas: (h.data || []).map(r => ({ id: r.id as string, nombre: r.nombre as string, orden: +r.orden || 0, archivada_en: (r.archivada_en as string | null) || null, trazos: limpiarTrazos(r.trazos) })),
    pendientes: filas.map(aPendiente),
  };
}

export async function guardarHoja(sb: SupabaseClient, h: Hoja) {
  revisar((await sb.from('todo_hojas').upsert({ id: h.id, nombre: h.nombre, orden: h.orden, archivada_en: h.archivada_en, trazos: h.trazos })).error);
}
export async function guardarPendiente(sb: SupabaseClient, p: PendienteTodo) {
  revisar((await sb.from('todo_pendientes').upsert({ id: p.id, hoja_id: p.hoja_id, texto: p.texto, orden: p.orden, hecho: p.hecho, fecha_seguimiento: p.fecha_seguimiento, archivado_en: p.archivado_en, pedido_id: p.pedido_id })).error);
}
export async function borrarPendiente(sb: SupabaseClient, id: string) { revisar((await sb.from('todo_pendientes').delete().eq('id', id)).error); }

// Lo mínimo para las alertas (sin dibujos): pendientes sin hacer y sin archivar con fecha hasta mañana, y las hojas.
export async function cargarResumenTodo(sb: SupabaseClient, manana: string): Promise<{ hojas: HojaTodo[]; pendientes: PendienteTodo[] }> {
  const [h, p] = await Promise.all([
    sb.from('todo_hojas').select('id, nombre, orden, archivada_en').order('orden'),
    sb.from('todo_pendientes').select(CAMPOS_P).eq('hecho', false).is('archivado_en', null).not('fecha_seguimiento', 'is', null).lte('fecha_seguimiento', manana).limit(500),
  ]);
  revisar(h.error); revisar(p.error);
  return { hojas: (h.data || []).map(r => ({ id: r.id as string, nombre: r.nombre as string, orden: +r.orden || 0, archivada_en: (r.archivada_en as string | null) || null })), pendientes: (p.data || []).map(aPendiente) };
}
export interface ResumenAlertas { alertas: FilaTodo[]; manana: FilaTodo[]; hojas: HojaTodo[] }
// Para el Tablero. Si la base aún no tiene las tablas, no hay alertas (y no se molesta a la usuaria).
export async function alertasDeTodo(sb: SupabaseClient, hoy: string, manana: string): Promise<ResumenAlertas> {
  try {
    const { hojas, pendientes } = await cargarResumenTodo(sb, manana);
    return { alertas: alertasTodo(pendientes, hojas, hoy), manana: paraManana(pendientes, hojas, hoy), hojas };
  } catch { return { alertas: [], manana: [], hojas: [] }; }
}

// Cambios puntuales desde el Tablero (posponer, marcar hecho): solo los campos que cambian, no toda la fila.
export async function actualizarPendiente(sb: SupabaseClient, id: string, parche: Partial<Pick<PendienteTodo, 'fecha_seguimiento' | 'hecho'>>) {
  revisar((await sb.from('todo_pendientes').update(parche).eq('id', id)).error);
}

// "Pasar a To Do's" desde una alerta del Tablero: un pendiente por pedido, con fecha, al final de la hoja elegida.
// Se salta el pedido que ya tiene uno igual sin hacer y sin archivar, para que el botón no duplique.
export async function crearDesdeAlerta(sb: SupabaseClient, alertaId: string, pedidos: string[], hojaId: string, fecha: string): Promise<{ creados: number; omitidos: number }> {
  const existentes: PendienteTodo[] = [];
  for (let i = 0; i < pedidos.length; i += 100) {
    const { data, error } = await sb.from('todo_pendientes').select(CAMPOS_P).in('pedido_id', pedidos.slice(i, i + 100)).eq('hecho', false).is('archivado_en', null);
    revisar(error); existentes.push(...(data || []).map(aPendiente));
  }
  const nuevos = nuevosDeAlerta(alertaId, pedidos, existentes), total = new Set(pedidos).size;
  if (!nuevos.length) return { creados: 0, omitidos: total };
  const u = await sb.from('todo_pendientes').select('orden').eq('hoja_id', hojaId).order('orden', { ascending: false }).limit(1);
  revisar(u.error);
  const base = +(u.data?.[0]?.orden ?? 0);
  revisar((await sb.from('todo_pendientes').insert(nuevos.map((n, i) => ({ hoja_id: hojaId, texto: n.texto, orden: base + 1 + i, fecha_seguimiento: fecha, pedido_id: n.pedido_id })))).error);
  return { creados: nuevos.length, omitidos: total - nuevos.length };
}
